/* eslint-disable react/destructuring-assignment */
import PropTypes from 'prop-types';
import React from 'react';
import {
  VictoryAxis, VictoryBar, VictoryChart, VictoryLegend, VictoryPie, VictoryTheme,
} from 'victory';
import { Alerta, Linha } from '../../styles/base';
import { graficosColorScale } from '../../styles/constants/colors';
import { isCorClara } from '../../util/color';
import {
  BordaTabela, CelulaTitulo, CelulaValor, ContainerGraficos, ContainerLegendas,
  IconeSecretaria, LinhaTabela,
} from './GraficoEstilo';

const formato = new Intl.NumberFormat('pt-BR');

const Grafico = ({ data, tipo }) => {
  if (!data) {
    return null;
  }

  if (!(data instanceof Array)) {
    return (
      <Alerta>Não há dados para exibir o gráfico.</Alerta>
    );
  }

  const porcentagem = data.reduce((total, { Valor }) => total + Valor, 0);

  if (tipo.Id !== 'ouvidoriaTempoResposta') {
    return (
      <>
        <GraficoBarra
          data={data.map(({ Label, Valor }) => {
            const valorPorcentado = (Number(Valor) * 100) / porcentagem;
            const valorFormatado = (valorPorcentado.toFixed(1).replace('.00', '').replace('.', ','));
            return ({
              valor: Valor,
              label: `${valorFormatado}%`,
              name: Label,
            });
          })}
          cores={tipo.cores}
        />
        <BordaTabela>
          {data.map(({ Label, Valor }) => (
            <LinhaTabela key={Label}>
              <CelulaTitulo key={Label}>{Label}</CelulaTitulo>
              <CelulaValor key={Valor}>{formato.format(Valor)}</CelulaValor>
            </LinhaTabela>
          ))}
        </BordaTabela>
      </>
    );
  }

  return (
    <BordaTabela>
      <LinhaTabela style={{ justifyContent: 'space-between' }}>
        <b>Secretaria</b>
        <b>Tempo de resposta</b>
      </LinhaTabela>
      {data.map(({
        Cor, Dias, Icone, Id, Secretaria,
      }) => {
        const imagemSecretaria = `https://eouve.com.br/svg_icons/${isCorClara(Cor) ? 'dark' : 'light'}/${Icone}.svg`;
        return (
          <GraficoTabela
            key={Id}
            cor={Cor}
            dias={Dias}
            secretaria={Secretaria}
            imagem={imagemSecretaria}
          />
        );
      })}
    </BordaTabela>
  );
};

const GraficoTabela = ({
  cor, dias, secretaria, imagem,
}) => (
  <LinhaTabela>
    <CelulaTitulo>
      <Linha>
        <IconeSecretaria style={{ backgroundColor: cor }}>
          <img
            width="30"
            height="30"
            src={imagem}
            alt="Secretaria"
          />
        </IconeSecretaria>
        <Linha style={{ maxWidth: 150 }}>
          <small style={{ fontSize: 16 }}>{secretaria}</small>
        </Linha>
      </Linha>
    </CelulaTitulo>
    {dias === 0
      ? (
        <CelulaValor>
          <small style={{ fontSize: 16 }}>No mesmo dia</small>
        </CelulaValor>
      )
      : (
        <CelulaValor>
          <small style={{ fontSize: 16 }}>
            {dias}
            {' '}
            {dias > 1 ? ('dias') : ('dia')}
          </small>
        </CelulaValor>
      )}

  </LinhaTabela>
);

const reverseData = (data, colors) => {
  const { length } = data;
  const newColors = [...colors];
  newColors.length = length;
  return [
    [...data].reverse(),
    newColors.reverse(),
  ];
};

const GraficoBarra = ({ data, cores }) => {
  const [dataReversa] = reverseData(data, cores);
  return (
    <ContainerGraficos>
      <VictoryChart
        domainPadding={{ x: 15 }}
        width={500}
        padding={{
          left: 90,
          right: 90,
          bottom: 20,
        }}
        height={data.length * 40}
        style={{
          labels: {
            fill: 'transparent',
            fontSize: 2,
          },
        }}
      >
        <VictoryAxis
          dependentAxis
          tickFormat={(thick) => formato.format(thick)}
          style={{
            grid: { stroke: '#dededd' },
            tickLabels: {
              fontSize: 7, padding: 5, fill: '#777', fontWeight: 'normal',
            },
          }}
        />
        <VictoryAxis
          style={{
            grid: { stroke: '#dededd' },
            tickLabels: {
              fontSize: 7, padding: 5, fill: '#777', fontWeight: 'normal',
            },
          }}
        />
        <VictoryBar
          horizontal
          barWidth={20}
          data={dataReversa}
          padding={{ top: 120, bottom: 60 }}
          x="name"
          y="valor"
          style={{
            labels: {
              fill: 'transparent',
              grid: { stroke: '#dededd' },
              tickLabels: {
                fontSize: 7, padding: 5, fill: '#777', fontWeight: 'normal',
              },
            },
            data: {
              fill: (d) => {
                if (d.datum.name === 'Aberto') {
                  const cor = '#ee796e';
                  return cor;
                } if (d.datum.name === 'Agendado') {
                  const cor = '#479fec';
                  return cor;
                } if (d.datum.name === 'Cancelado') {
                  const cor = '#999999';
                  return cor;
                } if (d.datum.name === 'Concluído') {
                  const cor = '#2dcc70';
                  return cor;
                } if (d.datum.name === 'Em andamento') {
                  const cor = '#f1c50e';
                  return cor;
                } if (d.datum.name === 'Pendente') {
                  const cor = '#f992f9';
                  return cor;
                } if (d.datum.name === 'Não respondido') {
                  const cor = '#ee796e';
                  return cor;
                } if (d.datum.name === 'Respondido') {
                  const cor = '#2dcc70';
                  return cor;
                }
                if (d.datum.name === 'Prazo em dia') {
                  const cor = '#54b87e';
                  return cor;
                } if (d.datum.name === 'Não Respondido') {
                  const cor = '#ee796e';
                  return cor;
                } if (d.datum.name === 'Finalizados') {
                  const cor = '#7ac9e6';
                  return cor;
                } if (d.datum.name === 'Respondido') {
                  const cor = '#54b87e';
                  return cor;
                } if (d.datum.name === 'Prazo atrasando') {
                  const cor = '#ebc014';
                  return cor;
                } if (d.datum.name === 'Prazo estourado') {
                  const cor = '#ee796e';
                  return cor;
                }
                if (d.datum.name === 'Total') {
                  const cor = '#3366ff';
                  return cor;
                } if (d.datum.name === 'Deferidos') {
                  const cor = '#54b87e';
                  return cor;
                } if (d.datum.name === 'Indeferidos') {
                  const cor = '#ee796e';
                  return cor;
                }
                if (d.datum.name === 'Sigilosos') {
                  const cor = '#ee796e';
                  return cor;
                } if (d.datum.name === 'Não Sigilosos') {
                  const cor = '#7ac9e6';
                  return cor;
                }
                return null;
              },
            },
          }}
        />
      </VictoryChart>
    </ContainerGraficos>
  );
};

const GraficoPizza = ({ data, cores }) => (
  <ContainerGraficos>
    <ContainerLegendas>
      <VictoryLegend
        orientation="horizontal"
        gutter={20}
        symbolSpacer={5}
        data={data}
        height={data.length * 5}
        theme={VictoryTheme.material}
        style={{
          labels: { fontSize: 7 },
          data: {
            fill: (d) => {
              if (d.datum.name === 'Aberto') {
                const cor = '#ee796e';
                return cor;
              } if (d.datum.name === 'Agendado') {
                const cor = '#479fec';
                return cor;
              } if (d.datum.name === 'Cancelado') {
                const cor = '#999999';
                return cor;
              } if (d.datum.name === 'Concluído') {
                const cor = '#2dcc70';
                return cor;
              } if (d.datum.name === 'Em andamento') {
                const cor = '#f1c50e';
                return cor;
              } if (d.datum.name === 'Pendente') {
                const cor = '#f992f9';
                return cor;
              } if (d.datum.name === 'Não respondido') {
                const cor = '#ee796e';
                return cor;
              } if (d.datum.name === 'Respondido') {
                const cor = '#2dcc70';
                return cor;
              }
              if (d.datum.name === 'Prazo em dia') {
                const cor = '#54b87e';
                return cor;
              } if (d.datum.name === 'Prazo atrasando') {
                const cor = '#ebc014';
                return cor;
              } if (d.datum.name === 'Prazo estourado') {
                const cor = '#ee796e';
                return cor;
              }
              if (d.datum.name === 'Total') {
                const cor = '#3366ff';
                return cor;
              } if (d.datum.name === 'Deferidos') {
                const cor = '#54b87e';
                return cor;
              } if (d.datum.name === 'Indeferidos') {
                const cor = '#ee796e';
                return cor;
              }
              return null;
            },
          },
        }}
      />
    </ContainerLegendas>

    <VictoryPie
      data={data}
      y="valor"
      startAngle={-90}
      endAngle={360}
      height={120}
      labelRadius={({ innerRadius }) => innerRadius + 10}
      padding={10}
      innerRadius={15}
      theme={VictoryTheme.material}
      colorScale={cores}
      style={{
        labels: {
          fontSize: 6,
          fill: '#fff',
          display: ({ datum }) => {
            const total = datum.label.replace('%', '');
            const totalFormat = total.replace(',', '.');
            const totalNumber = Number(totalFormat);
            if (totalNumber < '6') {
              return 'none';
            }

            return 'block';
          },
        },
        data: {
          fill: (d) => {
            if (d.slice.data.name === 'Aberto') {
              const cor = '#ee796e';
              return cor;
            } if (d.slice.data.name === 'Agendado') {
              const cor = '#479fec';
              return cor;
            } if (d.slice.data.name === 'Cancelado') {
              const cor = '#999999';
              return cor;
            } if (d.slice.data.name === 'Concluído') {
              const cor = '#2dcc70';
              return cor;
            } if (d.slice.data.name === 'Em andamento') {
              const cor = '#f1c50e';
              return cor;
            } if (d.slice.data.name === 'Pendente') {
              const cor = '#f992f9';
              return cor;
            } if (d.slice.data.name === 'Não respondido') {
              const cor = '#ee796e';
              return cor;
            } if (d.slice.data.name === 'Respondido') {
              const cor = '#2dcc70';
              return cor;
            } if (d.slice.data.name === 'Prazo em dia') {
              const cor = '#54b87e';
              return cor;
            } if (d.slice.data.name === 'Prazo atrasando') {
              const cor = '#ebc014';
              return cor;
            } if (d.slice.data.name === 'Prazo estourado') {
              const cor = '#ee796e';
              return cor;
            }
            if (d.datum.name === 'Total') {
              const cor = '#3366ff';
              return cor;
            } if (d.datum.name === 'Deferidos') {
              const cor = '#54b87e';
              return cor;
            } if (d.datum.name === 'Indeferidos') {
              const cor = '#ee796e';
              return cor;
            }
            return null;
          },
        },
      }}
    />
  </ContainerGraficos>
);

Grafico.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    Label: PropTypes.string,
    Valor: PropTypes.number,
  })).isRequired,
  tipo: PropTypes.oneOfType([PropTypes.object]),
};

Grafico.defaultProps = {
  tipo: null,
};

GraficoTabela.propTypes = {
  cor: PropTypes.string,
  dias: PropTypes.number,
  secretaria: PropTypes.string,
  imagem: PropTypes.string,
};

GraficoTabela.defaultProps = {
  secretaria: null,
  cor: null,
  dias: null,
  imagem: null,
};

GraficoPizza.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
    valor: PropTypes.number,
  })).isRequired,
  cores: PropTypes.oneOfType([PropTypes.array]),
};

GraficoPizza.defaultProps = {
  cores: graficosColorScale,
};

GraficoBarra.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
    valor: PropTypes.number,
  })).isRequired,
  cores: PropTypes.oneOfType([PropTypes.array]),
};

GraficoBarra.defaultProps = {
  cores: graficosColorScale,
};

export default Grafico;
